import { useCallback, useEffect, useMemo, useState } from "react"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import { navigate } from "gatsby"

import { useCartContext } from "@app/providers/cart"
import { useConfigContext } from "@app/providers/config"
import { useCustomerContext } from "@app/providers/customer"
import { useBraze } from "@app/hooks/useBraze"
import { useCore, useStorage } from "@app/hooks/useCore"
import { useCustomerBirthDate } from "@app/hooks/useCustomerBirthDate"
import { useFunctions } from "@app/hooks/useFunctions"
import { customerErrorMessage, useShopify } from "@app/hooks/useShopify"
import { ApiSuccessResponse, Verdict } from "@app/api/address-validation"

export const formatPhoneNumber = (phoneNumber: string, callingCode: string) => {
  // if phone number starts with 0, remove it
  let formattedPhoneNumber = phoneNumber.replace(/^0/, "")
  // align with calling code format
  formattedPhoneNumber = formattedPhoneNumber.slice(0, 1) !== "+" ? formattedPhoneNumber.replace(/^/, "+") : formattedPhoneNumber
  // check if phone number starts with calling code
  const formattedPhoneNumberWithCallingCode = formattedPhoneNumber.startsWith(callingCode)
    ? formattedPhoneNumber
    : `${callingCode}${formattedPhoneNumber}`

  return formattedPhoneNumberWithCallingCode.replaceAll(/\+/g, "")
}

export const useCustomerAccessToken = () => {
  const {
    graphql: {
      mutations: { CUSTOMER_ACCESS_TOKEN_CREATE, CUSTOMER_ACCESS_TOKEN_CREATE_MULTIPASS },
    },
  } = useCore()
  const { getCustomer, saveCustomer } = useCustomerContext()

  const [customerAccessTokenCreate] = useMutation(CUSTOMER_ACCESS_TOKEN_CREATE)
  const [customerAccessTokenCreateWithMultipass] = useMutation(CUSTOMER_ACCESS_TOKEN_CREATE_MULTIPASS)

  const createAccessToken = useCallback(
    async (email: string, password: string) => {
      if (email?.length && password?.length) {
        try {
          const {
            data: {
              customerAccessTokenCreate: { customerAccessToken, customerUserErrors },
            },
          } = await customerAccessTokenCreate({
            variables: { input: { email, password } },
          })

          if (customerAccessToken && !customerUserErrors?.length) saveCustomer(customerAccessToken)

          return { customerAccessToken, customerUserErrors }
        } catch (err) {
          console.error(err)
          return null
        }
      }
    },
    [customerAccessTokenCreate, saveCustomer]
  )

  const createAccessTokenWithMultipass = useCallback(
    async (multipassToken: string) => {
      try {
        const {
          data: {
            customerAccessTokenCreateWithMultipass: { customerAccessToken, customerUserErrors },
          },
        } = await customerAccessTokenCreateWithMultipass({
          variables: { multipassToken },
        })

        if (customerAccessToken && !customerUserErrors?.length) saveCustomer(customerAccessToken)

        return { customerAccessToken, customerUserErrors }
      } catch (err) {
        console.error(err)
        return null
      }
    },
    [customerAccessTokenCreateWithMultipass, saveCustomer]
  )

  return { createAccessToken, createAccessTokenWithMultipass, getCustomer }
}

export const useCustomerRegister = (turnstileToken?: string) => {
  const {
    helpers: { isBrowser, getUrlParameter, decodeShopifyId },
  } = useCore()
  const { callFunction } = useFunctions()
  const { identify, trackOnlineSignup } = useBraze()
  const { getCustomer, saveCustomer } = useCustomerContext()
  const {
    settings: { params, routes, keys },
    store,
  } = useConfigContext()
  const { setStorage } = useStorage()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Array<string>>([])
  const [data, setData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    acceptsMarketing: false,
    phone: "",
  })

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault()

    const filteredData = Object.entries(data).reduce(
      (a, [k, v]) => (v && !["passwordVerify"].includes(k as string) ? ((a[k as string] = v), a) : a),
      {} as Record<string, any>
    )

    await createCustomer({
      ...filteredData,
    })
  }

  const handleChange = ({ target: { type, name, value, checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setData(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const createCustomer = useCallback(
    async ({ ...userData }) => {
      setLoading(true)
      setErrors([])
      const filteredData = Object.entries(userData).reduce(
        (a, [k, v]) => (v && !["cfToken"].includes(k as string) ? ((a[k as string] = v), a) : a),
        {} as Record<string, any>
      )
      try {
        const registrationResponse = await callFunction("customer-register", {
          countryCode: store?.locationRegion,
          input: { ...filteredData },
          ["cf-turnstile-response"]: turnstileToken,
        })

        if (registrationResponse?.status === "error" && registrationResponse?.body) {
          setErrors([registrationResponse.body])
          setLoading(false)
          return
        }

        const errors = registrationResponse?.customerResponse?.data?.customerCreate?.customerUserErrors
        const accessToken = registrationResponse?.accessTokenResponse?.data?.customerAccessTokenCreate

        if (errors?.length || !registrationResponse?.customerResponse?.data?.customerCreate?.customer) {
          setErrors([customerErrorMessage(errors?.[0]) ?? "An unexpected error occurred. Please try again."])
          setLoading(false)
        } else {
          const path = getUrlParameter(params.continue)
          const route = path ? path : routes.DASHBOARD

          if (!accessToken?.customerUserErrors?.length && accessToken?.customerAccessToken) {
            const newCustomerId = registrationResponse?.customerResponse?.data?.customerCreate?.customer?.id
            const numericId = newCustomerId ? decodeShopifyId(newCustomerId, "Customer") : null
            if (numericId) {
              await identify(numericId, { logLoginEvent: false })
              await trackOnlineSignup({ source: "shopify_account" })
            }

            saveCustomer(accessToken.customerAccessToken)
            await getCustomer(accessToken.customerAccessToken)

            setStorage(keys.authenticating, "sign_up")

            isBrowser ? navigate(route, { replace: true }) : null
          } else {
            setErrors([customerErrorMessage(errors?.[0]) ?? "An unexpected error occurred. Please try again."])
            setLoading(false)
          }
        }
      } catch (error) {
        console.error(error)
        setErrors([(error as Error)?.message ?? "An unexpected error occurred. Please try again."])
        setLoading(false)
      }
    },
    [
      getUrlParameter,
      params.continue,
      routes.DASHBOARD,
      setStorage,
      keys.authenticating,
      isBrowser,
      turnstileToken,
      callFunction,
      store,
      saveCustomer,
      getCustomer,
      decodeShopifyId,
      identify,
      trackOnlineSignup,
    ]
  )

  return { createCustomer, handleSubmit, handleChange, data, loading, errors }
}

export const useCustomerLogin = () => {
  const {
    helpers: { getUrlParameter },
  } = useCore()
  const {
    settings: { keys, params, routes },
    store: { subfolder },
  } = useConfigContext()
  const { setStorage } = useStorage()
  const { gotoCheckout } = useCartContext()
  const { customer, saveCustomer } = useCustomerContext()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<(string | { message: string; field: any })[]>([])
  const [loggedIn, setLoggedIn] = useState(false)
  const [data, setData] = useState({ email: "", password: "" })
  const { callFunction } = useFunctions()
  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    await loginCustomer({
      ...data,
    })
  }

  useEffect(() => {
    const isCheckout = getUrlParameter(params.checkout)

    if (customer && isCheckout && loggedIn) {
      // Remove checkout param
      window.history.replaceState({}, document.title, window.location.pathname)
      gotoCheckout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, getUrlParameter, loggedIn, params.checkout])

  const handleChange = ({ target: { type, name, value, checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setData(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const loginCustomer = useCallback(
    async ({ ...userData }) => {
      setLoading(true)
      setErrors([])

      const path = getUrlParameter(params.continue)
      const route = path ? path?.replace(subfolder, "") : routes.DASHBOARD
      const isCheckout = getUrlParameter(params.checkout)

      try {
        const response = await callFunction("customer-global-login", { email: userData?.email, password: userData?.password })

        if (response?.status === "success" && response?.body?.local) {
          saveCustomer(response?.body?.local)
          setStorage(keys.authenticating, "login")
          setLoggedIn(true)

          if (!isCheckout) navigate(route, { replace: true })
        } else {
          setErrors([response?.body])
          setLoading(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setLoading(false)
      }
    },
    [
      getUrlParameter,
      params.continue,
      params.checkout,
      routes.DASHBOARD,
      callFunction,
      saveCustomer,
      setStorage,
      keys.authenticating,
      subfolder,
    ]
  )

  return { loginCustomer, data, handleSubmit, handleChange, loading, errors }
}

export const useCustomerLogout = () => {
  const { store } = useConfigContext()
  const {
    helpers: { isBrowser },
  } = useCore()

  const {
    settings: { keys },
  } = useConfigContext()

  const { clearCustomer } = useCustomerContext()
  const { removeStorage } = useStorage()
  const { logoutCleanup } = useBraze()

  const logoutCustomer = useCallback(async () => {
    await logoutCleanup()
    clearCustomer()
    removeStorage(keys.wishlistRegId)
    removeStorage(keys.wishlistSessionId)
    removeStorage(keys.wishlistSynced)
    if (isBrowser) navigate(`https://${store.shopifyCheckoutUrl}/account/logout`)
  }, [
    logoutCleanup,
    clearCustomer,
    removeStorage,
    keys.wishlistRegId,
    keys.wishlistSessionId,
    keys.wishlistSynced,
    isBrowser,
    store.shopifyCheckoutUrl,
  ])

  return { logoutCustomer }
}

export const useCustomerRecover = () => {
  const {
    graphql: {
      mutations: { CUSTOMER_RECOVER },
    },
  } = useCore()
  const { clearCustomer } = useCustomerContext()
  const [customerRecover] = useMutation(CUSTOMER_RECOVER)

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<(string | { message: string; field: any })[]>([])
  const [data, setData] = useState({ email: "" })
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    await recoverCustomer(data?.email)
  }

  const handleChange = ({ target: { type, name, value, checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setData(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const recoverCustomer = useCallback(
    async (email: string) => {
      setLoading(true)
      setErrors([])

      try {
        const {
          data: {
            customerRecover: { customerUserErrors },
          },
        } = await customerRecover({
          variables: { email },
        })

        if (!customerUserErrors?.length) {
          clearCustomer()
          setLoading(false)
          setSuccess(true)
        } else {
          setErrors(customerUserErrors)
          setLoading(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setLoading(false)
      }
    },
    [setLoading, setErrors, customerRecover, clearCustomer]
  )

  return { recoverCustomer, data, handleSubmit, handleChange, loading, errors, success }
}

export const useCustomerAccount = () => {
  const {
    helpers: { encodeShopifyId, isBrowser, getUrlParameter },
    graphql: {
      mutations: { CUSTOMER_RESET, CUSTOMER_ACTIVATE },
    },
  } = useCore()
  const {
    settings: { routes, params, keys },
  } = useConfigContext()
  const { saveCustomer } = useCustomerContext()
  const { setStorage } = useStorage()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<(string | { message: string; field: any })[]>([])
  const [data, setData] = useState({ password: "" })

  const [customerReset] = useMutation(CUSTOMER_RESET)
  const [customerActivate] = useMutation(CUSTOMER_ACTIVATE)

  const handleChange = ({ target: { type, name, value, checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setData(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const { resetCustomerId, resetToken } = useMemo(() => {
    const resetParams = getUrlParameter(params?.customer)?.split("/") || []
    const resetCustomerId = resetParams?.[0] || null
    const resetToken = resetParams?.[1] || null
    if (!resetCustomerId || !resetToken) setErrors(["Your reset password link is invalid."])
    return { resetCustomerId, resetToken }
  }, [getUrlParameter, params?.customer])

  const handleResetSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    await resetCustomer(resetCustomerId, resetToken, data?.password)
  }

  const resetCustomer = useCallback(
    async (customerId: string, resetToken: string, password: string) => {
      setLoading(true)
      setErrors([])

      try {
        const id = encodeShopifyId(customerId, "Customer")

        const {
          data: {
            customerReset: { customerAccessToken, customerUserErrors },
          },
        } = await customerReset({
          variables: { id, input: { resetToken, password } },
        })

        if (!customerUserErrors?.length) {
          saveCustomer(customerAccessToken)
          if (isBrowser) navigate(routes.DASHBOARD)
        } else {
          setErrors(customerUserErrors)
          setLoading(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setLoading(false)
      }
    },
    [encodeShopifyId, setLoading, setErrors, customerReset, saveCustomer, isBrowser, routes]
  )

  const { activateCustomerId, activationToken } = useMemo(() => {
    const activateParams = getUrlParameter(params?.customer)?.split("/") || []
    const activateCustomerId = activateParams?.[0] || null
    const activationToken = activateParams?.[1] || null
    if (!activateCustomerId || !activationToken) setErrors(["Your activate account link is invalid."])
    return { activateCustomerId, activationToken }
  }, [getUrlParameter, params?.customer])

  const handleActivateSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    await activateCustomer(activateCustomerId, activationToken, data?.password)
  }

  const activateCustomer = useCallback(
    async (customerId: string, activationToken: string, password: string) => {
      setLoading(true)
      setErrors([])

      try {
        const id = btoa(`gid://shopify/Customer/${customerId}`)

        const {
          data: {
            customerActivate: { customerAccessToken, customerUserErrors },
          },
        } = await customerActivate({
          variables: { id, input: { activationToken, password } },
        })

        if (!customerUserErrors?.length) {
          saveCustomer(customerAccessToken)
          setStorage(keys.authenticating, "sign_up")
          if (isBrowser) navigate(routes.DASHBOARD)
        } else {
          setErrors(customerUserErrors)
          setLoading(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setLoading(false)
      }
    },
    [customerActivate, saveCustomer, setStorage, keys.authenticating, isBrowser, routes.DASHBOARD]
  )

  return { resetCustomer, activateCustomer, data, setData, handleChange, handleActivateSubmit, handleResetSubmit, loading, errors }
}

export const useCustomerOrders = (first: number) => {
  const { store } = useConfigContext()
  const {
    graphql: {
      queries: { GET_CUSTOMER_ORDERS },
    },
  } = useCore()
  const { getStorage } = useStorage()
  const {
    settings: { keys },
  } = useConfigContext()
  const { orderNormaliser } = useShopify()
  const { accessToken: customerAccessToken } = getStorage(keys.customer) || { accessToken: "" }

  const { data, loading, error } = useQuery(GET_CUSTOMER_ORDERS, {
    variables: {
      countryCode: store?.locationRegion,
      customerAccessToken,
      first,
      reverse: true,
    },
  })

  const orders = orderNormaliser(data?.customer?.orders)?.filter((order: any) => order?.id)

  return { orders, loading, error }
}

export const useCustomerOrder = (orderId: string, key: string) => {
  const {
    helpers: { encodeBase64 },
    graphql: {
      queries: { GET_ORDER },
    },
  } = useCore()
  const { orderNormaliser } = useShopify()
  const id = encodeBase64(`gid://shopify/Order/${orderId}${key}`)

  const { data, loading, error } = useQuery(GET_ORDER, {
    variables: {
      id,
    },
  })

  const order = data?.node ? orderNormaliser(data?.node)?.[0] : null

  return { order, loading, error }
}

export const useCustomerAddress = (customer: any) => {
  const {
    helpers: { edgeNormaliser },
    graphql: {
      mutations: { CUSTOMER_ADDRESS_CREATE, CUSTOMER_ADDRESS_UPDATE, CUSTOMER_ADDRESS_DELETE, CUSTOMER_DEFAULT_ADDRESS_UPDATE },
      queries: { GET_CUSTOMER },
    },
  } = useCore()
  const { getStorage } = useStorage()
  const {
    store,
    settings: { keys },
  } = useConfigContext()
  const { addressNormaliser } = useShopify()
  const { setStorage } = useStorage()
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Array<string>>([])
  const initialData = useMemo(
    () => ({
      address1: "",
      address2: "",
      city: "",
      company: "",
      country: "Australia",
      firstName: "",
      lastName: "",
      phone: "",
      province: "",
      zip: "",
      default: false,
    }),
    []
  )
  const initialAddresses = customer?.addresses ? edgeNormaliser(customer?.addresses)?.map(addressNormaliser) : null
  const [address, setAddress] = useState({ ...initialData, id: "", action: "" })
  const [addresses, setAddresses] = useState(initialAddresses)
  const [addressValidationData, setAddressValidationData] = useState<ApiSuccessResponse | null>(null)
  const { accessToken: customerAccessToken } = getStorage(keys.customer) || { accessToken: "" }
  const { callFunction } = useFunctions()

  const [customerAddressCreate] = useMutation(CUSTOMER_ADDRESS_CREATE)
  const [customerAddressUpdate] = useMutation(CUSTOMER_ADDRESS_UPDATE)
  const [customerAddressDelete] = useMutation(CUSTOMER_ADDRESS_DELETE)
  const [customerDefaultAddressUpdate] = useMutation(CUSTOMER_DEFAULT_ADDRESS_UPDATE)

  const filterAndFormatData = useCallback(
    async (address: any) => {
      const excludedFields = ["default", "id", "action"]
      const filteredAddress = Object.keys(address)
        .filter(key => Object.keys(initialData).includes(key) && !excludedFields.includes(key))
        .reduce((obj: any, key: string) => {
          obj[key] = address[key]
          return obj
        }, {})

      try {
        const formattedAddress = {
          ...filteredAddress,
          phone: filteredAddress?.phone ? formatPhoneNumber(filteredAddress.phone, country_calling_code) : filteredAddress?.phone,
        }
        return formattedAddress
      } catch {
        return filteredAddress
      }
    },
    [initialData]
  )

  const [getAll, { data, loading }] = useLazyQuery(GET_CUSTOMER, {
    fetchPolicy: "no-cache",
    variables: {
      customerAccessToken,
      countryCode: store?.locationRegion,
    },
  })

  useEffect(() => {
    getAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // heavy handed fix for the above useEffect firing continiously, resulting in max depth exceeded...
  useEffect(() => {
    if (data?.customer) {
      const newAddresses = edgeNormaliser(data.customer.addresses).map((address: any) => ({
        ...address,
        default: address.id === data.customer.defaultAddress.id,
      }))

      if (JSON.stringify(newAddresses) !== JSON.stringify(addresses)) {
        setAddresses(newAddresses)
      }
    }
  }, [data, edgeNormaliser, addresses])

  const defaultAddress = useCallback(
    async addressId => {
      setSaving(true)
      setErrors([])

      try {
        const {
          data: {
            customerDefaultAddressUpdate: { customerUserErrors },
          },
        } = await customerDefaultAddressUpdate({
          variables: { addressId, customerAccessToken },
        })

        if (!customerUserErrors?.length) {
          setSaving(false)
          setStorage(keys.updatingAccount, "address")
        } else {
          setErrors(customerUserErrors)
          setSaving(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setSaving(false)
      }
    },
    [customerDefaultAddressUpdate, customerAccessToken, setStorage, keys.updatingAccount]
  )

  const validateAddressComponents = useCallback(
    async (address: any) => {
      const addressValidationPayload = {
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        province: address.province,
        country: address.country,
        zip: address.zip,
      }

      const { status, body } = await callFunction("address-validation", addressValidationPayload)

      if (status === "error") {
        console.error(body)
        setErrors(["Address couldn't be validated"])
        setSaving(false)
        return
      }

      if (status === "success") {
        const verdict: Verdict = body?.result?.verdict
        const missingComponents = body?.result?.address?.missingComponentsTypes

        if (
          verdict?.inputGranularity !== "PREMISE" &&
          verdict?.inputGranularity !== "SUB_PREMISE" &&
          verdict?.inputGranularity !== "OTHER"
        ) {
          setErrors(["Address must be a premises?"])
          setSaving(false)
          return false
        } else if (missingComponents) {
          setErrors([`Address is missing the following components: ${missingComponents.join(", ")}`])
          setSaving(false)
          return false
        } else if (!verdict?.addressComplete || verdict?.hasReplacedComponents) {
          setErrors(["The address doesn't seem quite right, is this what you meant?"])
          setAddressValidationData(body)
          setSaving(false)
          return false
        }

        return true
      }
    },
    [callFunction]
  )

  const createAddress = useCallback(
    async address => {
      setSaving(true)
      setErrors([])
      setAddressValidationData(null)

      try {
        const formattedAddress = await filterAndFormatData(address)

        const isAddressValid = await validateAddressComponents(formattedAddress)

        if (!isAddressValid) return

        const {
          data: {
            customerAddressCreate: { customerAddress, customerUserErrors },
          },
        } = await customerAddressCreate({
          variables: { customerAccessToken, address: formattedAddress },
        })

        if (address.default) await defaultAddress(customerAddress?.id)

        if (!customerUserErrors?.length) {
          getAll()
          setAddress({ ...initialData, id: "", action: "" })
          setStorage(keys.updatingAccount, "address")
          setSaving(false)
        } else {
          setErrors(customerUserErrors)
          setSaving(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setSaving(false)
      }
    },
    [
      filterAndFormatData,
      validateAddressComponents,
      customerAddressCreate,
      customerAccessToken,
      defaultAddress,
      getAll,
      initialData,
      setStorage,
      keys.updatingAccount,
    ]
  )

  const updateAddress = useCallback(
    async (id, address, isDefault) => {
      setSaving(true)
      setErrors([])
      setAddressValidationData(null)

      try {
        const formattedAddress = await filterAndFormatData(address)

        const isAddressValid = await validateAddressComponents(formattedAddress)

        if (!isAddressValid) return

        const {
          data: {
            customerAddressUpdate: { customerUserErrors },
          },
        } = await customerAddressUpdate({
          variables: { customerAccessToken, id, address: formattedAddress },
        })

        if (!customerUserErrors?.length) {
          if (isDefault) {
            await customerDefaultAddressUpdate({
              variables: { addressId: id, customerAccessToken },
            })
          }
          getAll()

          setAddress({ ...initialData, id: "", action: "" })
          setSaving(false)
          setStorage(keys.updatingAccount, "address")
        } else {
          setErrors(customerUserErrors)
          setSaving(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setSaving(false)
      }
    },
    [
      filterAndFormatData,
      validateAddressComponents,
      customerAddressUpdate,
      customerAccessToken,
      getAll,
      initialData,
      setStorage,
      keys.updatingAccount,
      customerDefaultAddressUpdate,
    ]
  )

  const deleteAddress = useCallback(
    async id => {
      setSaving(true)
      setErrors([])

      try {
        const {
          data: {
            customerAddressDelete: { customerUserErrors },
          },
        } = await customerAddressDelete({
          variables: { id, customerAccessToken },
        })

        if (!customerUserErrors?.length) {
          getAll()
          setSaving(false)
          setStorage(keys.updatingAccount, "address")
        } else {
          setErrors(customerUserErrors)
          setSaving(false)
        }
      } catch (err) {
        console.error(err)
        setErrors([(err as Error).message])
        setSaving(false)
      }
    },
    [customerAddressDelete, customerAccessToken, getAll, setStorage, keys.updatingAccount]
  )

  const updateAddressFields = useCallback(async () => {
    if (!addressValidationData) return
    const addressComponents = addressValidationData.result.address.addressComponents
    const suggestedAddressFields = addressValidationData.result.address.postalAddress

    const newAddress = {
      ...address,
      address1: suggestedAddressFields.addressLines[0],
      address2: suggestedAddressFields.addressLines?.[1] || "",
      city: suggestedAddressFields.locality,
      province: suggestedAddressFields.administrativeArea,
      zip: suggestedAddressFields.postalCode,
      country: addressComponents.find(c => c.componentType === "country")?.componentName?.text || "",
    }

    setAddress(newAddress)

    setAddressValidationData(null)
    setErrors([])

    address?.action === "update"
      ? await updateAddress(newAddress?.id, newAddress, address?.default)
      : address.action === "create"
        ? await createAddress(newAddress)
        : null
  }, [address, addressValidationData, createAddress, updateAddress])

  return {
    addresses,
    setAddress,
    address,
    createAddress,
    updateAddress,
    defaultAddress,
    deleteAddress,
    initialData,
    loading,
    saving,
    errors,
    updateAddressFields,
    addressValidationData,
  }
}

export const useCustomerDetails = ({ loadBirthDate = false }: { loadBirthDate?: boolean } = {}) => {
  useCustomerBirthDate(loadBirthDate)
  const { loginCustomer } = useCustomerLogin()
  const {
    graphql: {
      mutations: { CUSTOMER_UPDATE },
    },
  } = useCore()
  const { getStorage, setStorage } = useStorage()
  const {
    settings: { keys },
  } = useConfigContext()
  const { formatErrors } = useShopify()

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<any>()
  const [success, setSuccess] = useState(false)
  const initialData = useMemo(
    () => ({
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      acceptsMarketing: false,
      smsAcceptMarketing: false,
      birthDate: "",
    }),
    []
  )
  const [customerUpdate] = useMutation(CUSTOMER_UPDATE)
  const { customer: initialCustomer, setCustomer: saveCustomer } = useCustomerContext()
  const [customer, setCustomer] = useState(initialCustomer)
  const { accessToken: customerAccessToken } = getStorage(keys.customer) || { accessToken: "" }
  const { callFunction } = useFunctions()

  useEffect(() => {
    if (initialCustomer?.birthDate === undefined) return
    setCustomer((prev: any) => {
      if (!prev || prev.birthDate !== undefined) return prev
      return { ...prev, birthDate: initialCustomer.birthDate }
    })
  }, [initialCustomer])

  const filterData = useCallback(
    (data, hidePassword = false) => {
      return hidePassword
        ? Object.keys(data)
            .filter(key => Object.keys(initialData).includes(key))
            .filter(key => key !== "password")
            .reduce((obj: any, key: string) => {
              obj[key] = data[key]
              return obj
            }, {})
        : Object.keys(data)
            .filter(key => Object.keys(initialData).includes(key))
            .reduce((obj: any, key: string) => {
              obj[key] = data[key]
              return obj
            }, {})
    },
    [initialData]
  )

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    await updateCustomer(customer)
  }

  const handleChange = ({ target: { type, name, value, checked } }: React.ChangeEvent<HTMLInputElement>) =>
    setCustomer(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }))

  const updateCustomer = useCallback(
    async customer => {
      setLoading(true)
      setErrors([])

      try {
        const data = filterData(customer)
        const { smsAcceptMarketing, id, birthDate } = data
        delete data.smsAcceptMarketing
        delete data.id
        delete data.birthDate

        const {
          data: {
            customerUpdate: { customerUserErrors },
          },
        } = await customerUpdate({
          variables: { customerAccessToken, customer: data },
        })

        if (!customerUserErrors?.length) {
          const loadedBirthDate = initialCustomer?.birthDate
          const previousBirthDate = loadedBirthDate || null
          const hasBirthDate = birthDate !== undefined
          const nextBirthDate = birthDate || null
          let savedBirthDate = hasBirthDate ? nextBirthDate : loadedBirthDate
          let birthDateError = ""

          if (hasBirthDate && nextBirthDate !== previousBirthDate) {
            const birthDateResponse = await callFunction("customer-birth-date", {
              customerAccessToken,
              birthDate: nextBirthDate || "",
            })

            if (birthDateResponse?.status !== "success") {
              birthDateError = birthDateResponse?.body || "Could not save your birthday."
              savedBirthDate = loadedBirthDate
            } else {
              savedBirthDate = birthDateResponse.birthDate ?? null
            }
          } else if (hasBirthDate && loadedBirthDate === undefined) {
            // The field was cleared before the metafield finished loading. Leave it unset so that read can still land.
            savedBirthDate = undefined
          }

          const profile = filterData(customer, true)
          delete profile.birthDate
          saveCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            ...profile,
            ...(savedBirthDate !== undefined ? { birthDate: savedBirthDate } : {}),
          }))
          if (data?.password) {
            await loginCustomer({ email: data?.email, password: data?.password })
          }
          setStorage(keys.updatingAccount, "account")

          let smsError = false
          try {
            const smsResponse = await callFunction("sms-accept-marketing", { smsAcceptMarketing, id })
            if (!smsResponse.body) throw smsResponse
          } catch (error) {
            console.error(error)
            smsError = true
          }

          if (birthDateError || smsError) {
            setErrors({
              ...(birthDateError ? { birthDate: { message: birthDateError } } : {}),
              ...(smsError ? { message: `SMS accept marketing failed.` } : {}),
            })
            setLoading(false)
            return
          }

          setLoading(false)
          setSuccess(true)
        } else {
          setErrors(formatErrors(customerUserErrors))
          setLoading(false)
        }
      } catch (err) {
        setErrors(formatErrors(err))
        setLoading(false)
      }
    },
    [
      filterData,
      customerUpdate,
      customerAccessToken,
      saveCustomer,
      initialCustomer,
      setStorage,
      keys.updatingAccount,
      callFunction,
      loginCustomer,
      formatErrors,
    ]
  )

  return { customer, setCustomer, updateCustomer, handleSubmit, handleChange, loading, success, errors }
}
